import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';

// Mirrors the backend DEFAULTS, so the very first paint (and the prerendered HTML)
// already shows real content; the live admin-managed values replace it a moment later.
export const DEFAULT_CONFIG = {
  pricing: {
    pro_price_inr: 99, original_price_inr: 0, period_label: 'month', badge: '',
    free_features: [
      { text: '1 Vehicle QR Code', included: true }, { text: 'Asset QR Codes (PRO)', included: false },
      { text: 'Pet QR Codes (PRO)', included: false }, { text: 'Basic contact requests', included: true },
      { text: 'Email support', included: true },
    ],
    pro_features: [
      { text: 'Multiple Vehicle QR Codes', included: true }, { text: 'Multiple Asset QR Codes', included: true },
      { text: 'Multiple Pet QR Codes', included: true }, { text: 'Priority Support', included: true },
      { text: 'Advanced Features', included: true },
    ],
  },
  features: [
    { icon: '📞', title: 'WebRTC Voice/Video Calling', text: 'Real-time calls between finder and owner. No phone numbers exchanged!' },
    { icon: '🔔', title: 'Push Notifications', text: 'Get instant alerts with sound when someone scans your QR code.' },
    { icon: '📱', title: 'PWA Support', text: 'Install as mobile app. Works offline with service worker caching.' },
    { icon: '💳', title: 'Secure Payments', text: 'Secure payments for PRO plan. Just ₹{price}/{period} for multiple QR codes!' },
    { icon: '🔒', title: 'Privacy First', text: 'Anonymous communication. Your contact details stay private.' },
    { icon: '⚡', title: 'Lightning Fast', text: 'Optimized for speed. Instant QR scanning and real-time updates.' },
  ],
  testimonials: [],
  maintenance: { enabled: false, message: '', eta: '', keep_finder_working: true },
  announcement: { enabled: false },
  contact: { whatsapp: '', email: 'business@dotlinkapp.com' },
  limits: { vehicles: 1, assets: 0, pets: 0 },
  partner_plan: { enabled: true, price_per_qr_inr: 3, min_qr: 50 },
  demo: { video_url: '' },
  analytics: { ga_measurement_id: '' },
  business: { legal_name: '', address: '', phone: '', gstin: '', grievance_officer: '' },
};

const Ctx = createContext({ config: DEFAULT_CONFIG, refresh: () => {} });
const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export const SiteConfigProvider = ({ children }) => {
  // The prerender step embeds the live config in the page (window.__SITE_CONFIG__), so even the
  // very first paint and no-JavaScript crawlers see current content, not just the defaults.
  const [config, setConfig] = useState(() => ({ ...DEFAULT_CONFIG, ...(typeof window !== 'undefined' && window.__SITE_CONFIG__ ? window.__SITE_CONFIG__ : {}) }));

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/site/config`);
      setConfig({ ...DEFAULT_CONFIG, ...res.data });
    } catch {
      // keep whatever we have (defaults or the last good config)
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);                         // open pages pick up admin changes within ~30 s
    const onEvent = () => refresh();
    // Coming back to the tab / reopening the installed app refreshes immediately
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onEvent);
    window.addEventListener('dl-maintenance', onEvent);            // fired when any API call answers 503
    window.addEventListener('dl-config-changed', onEvent);         // fired by the admin editor after saving
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onEvent);
      window.removeEventListener('dl-maintenance', onEvent);
      window.removeEventListener('dl-config-changed', onEvent);
    };
  }, [refresh]);

  return React.createElement(Ctx.Provider, { value: { config, refresh } }, children);
};

export const useSiteConfig = () => useContext(Ctx);

// "{price}" and "{period}" tokens in admin-written text follow the live price.
export const fillTokens = (text, pricing) =>
  String(text || '').replace('{price}', pricing.pro_price_inr).replace('{period}', pricing.period_label);
